"use client";

import {
	RiCalendarLine,
	RiGovernmentLine,
	RiMapPinLine,
	RiNewspaperLine,
	RiQuillPenAiLine,
	RiQuillPenLine,
	RiTicket2Line,
	RiTreasureMapLine,
} from "@remixicon/react";
import { formatFestivalDatesWithPreferences } from "@workspace/shared-utils";
import type { FestivalDetails } from "@workspace/types";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { Card, CardHeader } from "@workspace/ui/components/card";
import { memo, useCallback, useMemo, useState } from "react";
import { AuthPromptModal } from "@/components/auth/auth-prompt-modal";
import { FestivalStatusButton } from "@/components/features/festival/buttons/festival-status-button";
import { FestivalStatusDisplay } from "@/components/features/festival/buttons/festival-status-display";
import { FestivalReviewModalReadOnly } from "@/components/features/festival/modals/festival-review-modal-read-only";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { FestivalPoster } from "@/components/shared/photos/festival-poster";
import { SocialButtonGroup } from "@/components/shared/social-button-group";
import { StarRating } from "@/components/shared/star-rating";
import { VenueDetailModal } from "@/components/shared/venue-detail-modal";
import { FestivalMapsModal } from "@/components/features/festival/modals/festival-maps-modal";
import { useUserFestivalStatusQuery } from "@/hooks/features/festival/use-user-festival-status-query";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import {
	hasFestivalReviewData,
	type TrpcFestivalReview,
} from "@/lib/features/festival/review-data-transform";
import { trpc } from "@/lib/trpc/react";

interface FestivalDetailProps {
	festival: FestivalDetails;
	readOnly?: boolean;
	externalReview?: TrpcFestivalReview | null | undefined;
	profileOwnerUserId?: string; // For fetching profile owner's festival status
	onOpenReview?: (festivalId: string) => void;
}

/**
 * Festival Detail component for displaying festival information and review actions.
 *
 * Features:
 * - Festival poster, name, location, and dates
 * - User's festival review (if exists)
 * - Write/Edit review functionality
 * - Read-only mode for viewing other users' reviews
 */

const FestivalDetailCard = memo(
	function FestivalDetailCard({
		festival,
		readOnly = false,
		externalReview,
		profileOwnerUserId,
		onOpenReview,
	}: FestivalDetailProps) {
		const { user } = useSupabase();
		const [authModalOpen, setAuthModalOpen] = useState(false);
		const [authModalAction, setAuthModalAction] = useState<
			"add_to_list" | "general"
		>("general");
		const [showReadOnlyModal, setShowReadOnlyModal] = useState(false);
		const [showVenueModal, setShowVenueModal] = useState(false);
		const [showMapsModal, setShowMapsModal] = useState(false);
		const [showLocationMapModal, setShowLocationMapModal] = useState(false);

		// Get user preferences for date formatting
		const userPreferences = useUserPreferences();

		// Memoize user ID to stabilize queries
		const userId = useMemo(() => user?.id ?? undefined, [user?.id]);


		// Use external review from parent component (supports local state for instant updates)
		const reviewData = externalReview;

		// Get user's festival status (social broadcasting) - using query hook
		const { status: festivalStatus } = useUserFestivalStatusQuery(
			userId,
			festival.id,
		);

		// Get profile owner's festival status when viewing someone else's profile
		const { data: profileOwnerData } =
			trpc.festival.getProfileOwnerStatus.useQuery(
				{
					profileUserId: profileOwnerUserId || "",
					festivalId: festival.id,
				},
				{
					enabled: !!readOnly && !!profileOwnerUserId && !!festival.id,
				},
			);

		const profileOwnerStatus = profileOwnerData?.status || null;

		// Check if festival is past - memoize to prevent recalculation
		const festivalIsPast = useMemo(() => {
			if (!festival.end_date) return false;

			const parts = festival.end_date.split("-");
			const year = Number(parts[0]);
			const month = Number(parts[1]);
			const day = Number(parts[2]);
			const endDate = new Date(year, month - 1, day);
			endDate.setHours(23, 59, 59, 999);

			return endDate < new Date();
		}, [festival.end_date]);


		const handleReviewClick = useCallback(() => {
			if (!user) {
				setAuthModalAction("general");
				setAuthModalOpen(true);
				return;
			}
			if (onOpenReview) {
				onOpenReview(festival.id);
			}
		}, [user, onOpenReview, festival.id]);

		const handleAuthRequired = useCallback(() => {
			setAuthModalAction("add_to_list");
			setAuthModalOpen(true);
		}, []);

		// Handle festival status change - button handles everything now
		const handleStatusChange = useCallback(() => {
			// Button component handles all updates and cache invalidation
			// Nothing needed here anymore
		}, []);

		return (
			<Card className="h-full">
				<CardHeader className="p-3">
					<div className="relative grid grid-cols-[auto_1fr] gap-4">
						{/* Festival poster - always visible on left, responsive sizing */}
						<div className="relative w-32 md:w-40 lg:w-48">
							<FestivalPoster
								name={festival.name}
								posterUrl={festival.poster_url || null}
								size="full"
								rounded="lg"
								priority={true}
							/>
						</div>

						{/* Festival info */}
						<div className="min-w-0">
							<div className="mb-2 flex items-center gap-2">
								<h1 className="font-bold text-xl">{festival.name}</h1>
								{festival.is_cancelled && (
									<Badge className="border-red-200 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-900 dark:text-red-200">
										Cancelled
									</Badge>
								)}
							</div>
							<div className="flex flex-col gap-3">
								{festival.venue_obj && (
									<div className="flex items-center gap-2 text-base">
										<RiGovernmentLine className="h-4 w-4 flex-shrink-0" />
										<button
											type="button"
											onClick={() => setShowVenueModal(true)}
											className="cursor-pointer leading-tight text-left text-primary underline hover:text-primary/80"
										>
											{festival.venue_obj.name}
										</button>
									</div>
								)}
								{festival.venue_obj && (
									<div className="flex items-center gap-2 text-base">
										<RiMapPinLine className="h-4 w-4 flex-shrink-0" />
										<button
											type="button"
											onClick={() => setShowLocationMapModal(true)}
											className="cursor-pointer leading-tight text-left underline text-primary hover:text-primary/80"
										>
											{festival.location}
										</button>
									</div>
								)}
								{/* Festival Maps Row */}
								{festival.map_urls && festival.map_urls.length > 0 && (
									<div className="flex items-center gap-2 text-base">
										<RiTreasureMapLine className="h-4 w-4 flex-shrink-0" />
										<button
											type="button"
											onClick={() => setShowMapsModal(true)}
											className="cursor-pointer leading-tight text-left text-primary underline hover:text-primary/80"
										>
											Festival Maps
										</button>
									</div>
								)}
								<div className="flex items-center gap-2 text-base">
									<RiCalendarLine className="h-4 w-4 flex-shrink-0" />
									<span className="leading-tight text-left">
										{formatFestivalDatesWithPreferences(
											festival.start_date,
											festival.end_date,
											userPreferences,
										)}
									</span>
								</div>

								{/* Show user's overall rating only if review exists and has rating */}
								{reviewData &&
									"overallRating" in reviewData &&
									reviewData.overallRating > 0 && (
										<div className="flex items-center gap-2 text-base">
											<StarRating
												rating={Number(reviewData.overallRating)}
												size="sm"
												readOnly={true}
											/>
										</div>
									)}

								{/* Show user review content if available */}
								{reviewData &&
									"content" in reviewData &&
									reviewData.content &&
									reviewData.content.trim().length > 0 && (
										<div className="text-muted-foreground text-sm italic">
											"
											{reviewData.content.length > 150
												? `${reviewData.content.substring(0, 150)}...`
												: reviewData.content}
											"
										</div>
									)}

								{/* External links row: Website, Store, YouTube, Playlist */}
								<SocialButtonGroup
									name={festival.name}
									links={{
										website: festival.website_url,
										store: festival.store_url,
										instagram: festival.instagram_url,
										facebook: festival.facebook_url,
										twitter: festival.twitter_url,
										tiktok: festival.tiktok_url,
										spotify: festival.spotify_url,
										youtube: festival.youtube_url,
										playlist: festival.playlist_url,
									}}
									size="xs"
								/>

								{/* Buttons: 3 in a row, wrap if needed */}
								<div className="flex flex-wrap gap-2">
									{/* Festival Status button - shown to all users, opens auth modal for non-auth */}
									{!readOnly && (
										<FestivalStatusButton
											festivalId={festival.id}
											currentStatus={festivalStatus}
											festivalIsPast={festivalIsPast}
											size="sm"
											onStatusChange={handleStatusChange}
											onAuthRequired={handleAuthRequired}
										/>
									)}

									{/* Read-only Festival Status display */}
									{readOnly && profileOwnerStatus && (
										<FestivalStatusDisplay
											currentStatus={profileOwnerStatus}
											festivalIsPast={festivalIsPast}
											size="sm"
										/>
									)}

									{/* Read Review button */}
									{readOnly &&
										reviewData &&
										hasFestivalReviewData(reviewData) && (
											<Button
												size="sm"
												variant="secondary"
												onClick={() => setShowReadOnlyModal(true)}
											>
												<RiNewspaperLine className="h-4 w-4" />
												Read Review
											</Button>
										)}

									{/* Write/Edit Review button */}
									{!readOnly && (
										<Button
											size="sm"
											variant="secondary"
											onClick={handleReviewClick}
										>
											{reviewData && hasFestivalReviewData(reviewData) ? (
												<RiQuillPenLine className="h-4 w-4" />
											) : (
												<RiQuillPenAiLine className="h-4 w-4" />
											)}
											{reviewData && hasFestivalReviewData(reviewData)
												? "Edit Review"
												: "Write Review"}
										</Button>
									)}

									{/* Buy Ticket button */}
									{festival.ticket_url && (
										<Button size="sm" variant="default" asChild>
											<a
												href={festival.ticket_url}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-2"
											>
												<RiTicket2Line className="h-4 w-4" />
												<span>Buy Ticket</span>
											</a>
										</Button>
									)}
								</div>
							</div>
						</div>
					</div>
				</CardHeader>

				{/* Read-only review modal */}
				<FestivalReviewModalReadOnly
					isOpen={showReadOnlyModal}
					festival={festival}
					userReview={
						reviewData
							? {
									id: reviewData.id,
									festivalId: reviewData.festivalId,
									userId: reviewData.userId,
									overallRating: reviewData.overallRating,
									vibeRating: reviewData.vibeRating,
									productionRating: reviewData.productionRating,
									logisticsRating: reviewData.logisticsRating,
									foodRating: reviewData.foodRating,
									content: reviewData.content,
									updatedAt: reviewData.updatedAt,
								}
							: null
					}
					onClose={() => setShowReadOnlyModal(false)}
				/>

				{/* Auth prompt modal */}
				<AuthPromptModal
					open={authModalOpen}
					onOpenChange={setAuthModalOpen}
					actionType={authModalAction}
					redirectPath={`/festivals/${festival.slug}`}
				/>

				{/* Venue detail modal */}
				{festival.venue_obj && (
					<VenueDetailModal
						isOpen={showVenueModal}
						venue={festival.venue_obj}
						onClose={() => setShowVenueModal(false)}
						showUpcomingShowsButton={false}
					/>
				)}

				{/* Festival Maps modal */}
				{festival.map_urls && festival.map_urls.length > 0 && (
					<FestivalMapsModal
						isOpen={showMapsModal}
						onClose={() => setShowMapsModal(false)}
						mapUrls={festival.map_urls}
						festivalName={festival.name}
					/>
				)}

				{/* Location Map modal */}
				{festival.venue_obj && (
					<VenueDetailModal
						isOpen={showLocationMapModal}
						venue={festival.venue_obj}
						onClose={() => setShowLocationMapModal(false)}
						showUpcomingShowsButton={false}
						initialView="map"
					/>
				)}
			</Card>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison for memo optimization
		return (
			prevProps.festival.id === nextProps.festival.id &&
			prevProps.readOnly === nextProps.readOnly &&
			prevProps.externalReview === nextProps.externalReview &&
			prevProps.onOpenReview === nextProps.onOpenReview
		);
	},
);

FestivalDetailCard.displayName = "FestivalDetailCard";

export { FestivalDetailCard };
